/**
 * V70G6B — 엔글 오목 FE Hook.
 *
 * - onSnapshot 으로 omokRoomsPublic/{roomId} 구독 (BracketDetailPage.tsx:38-61 패턴)
 * - Callable mutations: create/join/play/resign/leave
 * - 중복 요청 방지: in-flight guard (ref)
 * - 새로고침 복귀: localStorage 로 lastRoomId 저장
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, getFunctions } from '../../../firebase/utils';
import type { OmokPublicRoom, OmokMoveResultServer } from './types';
import { activeGameRoomIds, toOmokErrorMessage } from './omokView';

import { clearLastRoomIdIf, isOmokRoomEnded, loadLastRoomId, saveLastRoomId } from './omokLastRoom';

const COLL = 'omokRoomsPublic';

function newReqId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// V70G8J — 복귀 참조 저장·정리는 omokLastRoom 으로 이동 (기존 import 경로 유지용 re-export)
export { saveLastRoomId, loadLastRoomId, isOmokRoomEnded, clearLastRoomIdIf };

/**
 * V70G8J — 로비 복귀 안내 대상 확인.
 *   저장된 방을 공개 문서로 1회 조회해 종료(finished/abandoned)·삭제된 방이면 참조를 지우고 null.
 *   진행 중/대기 중이면 roomId 반환. 조회 실패(네트워크 등)는 기존 동작대로 안내 유지.
 *   서버 쪽 진행 중 대국 판정(omokActivePlayers)은 이미 종료 방을 무시하므로 서버 정리는 불필요.
 */
export async function resolveResumableRoomId(): Promise<string | null> {
  const last = loadLastRoomId();
  if (!last) return null;
  try {
    const snap = await getDoc(doc(getDb(), COLL, last));
    if (!snap.exists() || isOmokRoomEnded((snap.data() as OmokPublicRoom).status)) {
      clearLastRoomIdIf(last);
      return null;
    }
    return last;
  } catch (e) {
    console.warn('[omok] last room check failed:', e);
    return last;
  }
}

/**
 * onSnapshot 으로 방 상태를 구독하는 hook.
 */
export function useOmokRoomSubscription(roomId: string | null): {
  room: OmokPublicRoom | null;
  loading: boolean;
  error: string | null;
} {
  const [room, setRoom] = useState<OmokPublicRoom | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!roomId) {
      setRoom(null); setLoading(false); setError(null);
      return;
    }
    setLoading(true); setError(null);
    const db = getDb();
    const unsub = onSnapshot(
      doc(db, COLL, roomId),
      (snap) => {
        if (!snap.exists()) {
          setRoom(null);
          setError('방이 존재하지 않습니다.');
          setLoading(false);
          return;
        }
        setRoom(snap.data() as OmokPublicRoom);
        setLoading(false);
      },
      (err) => {
        console.warn('[omok] onSnapshot error:', err);
        setError(err?.message || String(err));
        setLoading(false);
      },
    );
    return () => unsub();
  }, [roomId]);

  return { room, loading, error };
}

export interface OmokParticipation {
  side: 'black' | 'white' | null;
  publicPlayerId: string | null;
  serverNowMs?: number;
  /** V70G8E — 이 방이 아닌 곳에서 진행 중인 내 대국 (서버 확인) */
  activeRoomIds?: string[];
}

/**
 * Callable mutation API wrapper.
 *   각 action 마다 in-flight 가드 (ref) 로 중복 요청 차단.
 */
export function useOmokActions(): {
  // V70G8E — resignRoomIds: 진행 중 대국 기권 확인 후 재요청 시에만 전달 (서버가 같은 트랜잭션에서 기권 + 입장)
  createRoom: (resignRoomIds?: string[]) => Promise<{ roomId: string; roomCode: string; publicPlayerId: string }>;
  joinRoom: (roomCode: string, resignRoomIds?: string[]) => Promise<{ roomId: string; publicPlayerId: string }>;
  playMove: (roomId: string, x: number, y: number) => Promise<OmokMoveResultServer>;
  resignRoom: (roomId: string) => Promise<{ winner: 'black' | 'white'; winReason: 'resign' }>;
  leaveRoom: (roomId: string) => Promise<{ ok: true }>;
  // V70G6C — 서버 UID 기반 side 조회 (displayName 비교 금지)
  getMyParticipation: (roomId: string) => Promise<OmokParticipation>;
  // V70G8D — 카운트다운 0초 시 서버에 시간 초과 확정 요청 (서버가 마감 직접 검증)
  claimTimeout: (roomId: string) => Promise<{ finalized: boolean; serverNowMs?: number; turnDeadlineMs?: number | null }>;
  busy: boolean;
  error: string | null;
  clearError: () => void;
} {
  const inflightRef = useRef<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const runGuarded = async <T,>(fn: () => Promise<T>): Promise<T> => {
    if (inflightRef.current) {
      throw new Error('이전 요청이 처리 중입니다. 잠시 후 다시 시도하세요.');
    }
    inflightRef.current = true;
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e: any) {
      // V70G8E — 진행 중 대국 확인이 필요한 경우는 오류 박스 대신 화면이 확인 팝업으로 처리
      if (!activeGameRoomIds(e)) setError(toOmokErrorMessage(e));
      throw e;
    } finally {
      inflightRef.current = false;
      setBusy(false);
    }
  };

  const createRoom = useCallback((resignRoomIds?: string[]) => runGuarded(async () => {
    const fns = getFunctions();
    const call = httpsCallable<{ clientRequestId: string; resignRoomIds?: string[] }, { roomId: string; roomCode: string; publicPlayerId: string; reused?: boolean }>(fns, 'createOmokRoom');
    const r = await call({ clientRequestId: newReqId('create'), ...(resignRoomIds?.length ? { resignRoomIds } : {}) });
    return r.data;
  }), []);

  const joinRoom = useCallback((roomCode: string, resignRoomIds?: string[]) => runGuarded(async () => {
    const fns = getFunctions();
    const call = httpsCallable<{ roomCode: string; clientRequestId: string; resignRoomIds?: string[] }, { roomId: string; publicPlayerId: string; reused?: boolean }>(fns, 'joinOmokRoom');
    const r = await call({ roomCode, clientRequestId: newReqId('join'), ...(resignRoomIds?.length ? { resignRoomIds } : {}) });
    return r.data;
  }), []);

  const playMove = useCallback((roomId: string, x: number, y: number) => runGuarded(async () => {
    const fns = getFunctions();
    const call = httpsCallable<{ roomId: string; x: number; y: number; clientRequestId: string }, OmokMoveResultServer>(fns, 'playOmokMove');
    const r = await call({ roomId, x, y, clientRequestId: newReqId('mv') });
    return r.data;
  }), []);

  const resignRoom = useCallback((roomId: string) => runGuarded(async () => {
    const fns = getFunctions();
    const call = httpsCallable<{ roomId: string; clientRequestId: string }, { winner: 'black' | 'white'; winReason: 'resign' }>(fns, 'resignOmokRoom');
    const r = await call({ roomId, clientRequestId: newReqId('resign') });
    return r.data;
  }), []);

  const leaveRoom = useCallback((roomId: string) => runGuarded(async () => {
    const fns = getFunctions();
    const call = httpsCallable<{ roomId: string; clientRequestId: string }, { ok: true }>(fns, 'leaveOmokRoom');
    const r = await call({ roomId, clientRequestId: newReqId('leave') });
    return r.data;
  }), []);

  // V70G6C — 플레이어 식별 (서버 UID 기반, displayName 비교 금지)
  //   읽기 전용이라 in-flight 가드 미적용 (여러 번 호출 가능).
  const getMyParticipation = useCallback(async (roomId: string) => {
    const fns = getFunctions();
    const call = httpsCallable<{ roomId: string }, OmokParticipation>(fns, 'getMyOmokRoomParticipation');
    const r = await call({ roomId });
    return r.data;
  }, []);

  // V70G8D — 시간 초과 확정 요청. 착수와 독립 (in-flight 가드 미적용, 서버가 멱등 처리)
  const claimTimeout = useCallback(async (roomId: string) => {
    const fns = getFunctions();
    const call = httpsCallable<{ roomId: string }, { finalized: boolean; serverNowMs?: number; turnDeadlineMs?: number | null }>(fns, 'claimOmokTimeout');
    const r = await call({ roomId });
    return r.data;
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { createRoom, joinRoom, playMove, resignRoom, leaveRoom, getMyParticipation, claimTimeout, busy, error, clearError };
}
